import {
  Alert01Icon,
  ArtificialIntelligence08Icon,
  Calendar01Icon,
  CheckmarkCircle02Icon,
  Clock01Icon,
  ComputerIcon,
  InformationCircleIcon,
  Linkedin01Icon,
  Loading03Icon,
  Mail01Icon,
  SmartPhone01Icon,
  ThumbsUpIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';

const DEFAULT_SAMPLE_REFEREE = {
  name: 'Brian Gastesi',
  initials: 'Br',
  email: 'engineering@checkmate.tech',
  contact_number: '+3412312312',
  ip: '121.99.241.144',
  linkedin_profile: '',
  has_company_email: false,
  response_date: "26 Aug, '23",
  completed_in: '6 hours 48 mins',
};

const DEFAULT_SAMPLE_CANDIDATE = {
  name: 'Brian gastesi',
  job_role: 'Chief Technology Officer',
  department: 'Product Team',
  email: 'engineering@checkmate.tech',
  contact_number: '+64 123 123 1234',
  ip: '2803:9800:9842:6659',
};

const DEFAULT_SAMPLE_ANSWER =
  'Sam consistently demonstrated excellent communication skills across both internal and external stakeholders. He was proactive, collaborative, and always maintained a positive attitude when working under pressure. He effectively communicated complex technical concepts to non-technical audiences, ensuring clear understanding and alignment on project goals. Additionally, his ability to build strong relationships and foster a positive work environment contributed to a high-performing team dynamic. His communication style was clear, concise, and always focused on delivering results while maintaining a positive and professional demeanor.';

const DEFAULT_SAMPLE_QUESTIONS = [
  {
    order: 1,
    type_label: 'Text',
    text: 'Can you describe the candidate’s communication and teamwork skills based on your experience working with them?',
    answer: DEFAULT_SAMPLE_ANSWER,
    referee: DEFAULT_SAMPLE_REFEREE,
  },
];

const DEFAULT_SAMPLE_CHECK = {
  created_at: "15 May, '26",
  completed_at: "15 May, '26",
  completed_in: '9 mins',
  anonymize_referee: false,
};

const CandidateField = ({ label, value }) => (
  <div className="flex flex-col gap-1 w-[calc(50%-8px)] min-w-[180px]">
    <p className="text-lg font-medium uppercase tracking-wide m-0" style={{ color: '#62748e' }}>
      {label}
    </p>
    <p className="text-lg font-medium m-0 text-neutral-7">{value}</p>
  </div>
);

const RefereeDetailRow = ({ icon, children }) => (
  <div className="flex flex-row items-center gap-2 flex-wrap">
    <HugeiconsIcon icon={icon} size={16} className="shrink-0 text-neutral-6" strokeWidth={2} />
    {children}
  </div>
);

const RefereeWarning = ({ text }) => (
  <div className="inline-flex items-center gap-1 flex-grow-1">
    <HugeiconsIcon
      icon={Alert01Icon}
      size={16}
      className="shrink-0 text-[#eb5757]"
      strokeWidth={2}
    />
    <span className="text-lg font-medium text-[#F2994A]">{text}</span>
  </div>
);

const VerifiedBadge = () => (
  <HugeiconsIcon
    icon={CheckmarkCircle02Icon}
    size={16}
    className="shrink-0 text-green-2"
    strokeWidth={2}
  />
);

const ReportMetadataField = ({ icon, label, children, borderColor }) => (
  <div className="flex flex-col gap-1 pl-4 border-l-2" style={{ borderColor }}>
    <div className="flex flex-row gap-2 items-center">
      <HugeiconsIcon icon={icon} size={16} className="shrink-0 text-neutral-6" strokeWidth={2} />
      <span className="text-lg font-normal uppercase" style={{ color: 'rgb(98, 116, 142)' }}>
        {label}
      </span>
    </div>
    {children}
  </div>
);

const getRefereeInitials = name => {
  const parts = name?.trim().split(/\s+/) ?? [];
  return parts
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() ?? '')
    .join('');
};

const getQuestionTypeLabel = question => question?.type_label || question?.type || 'Text';

const renderQuestionText = (text, color) => {
  if (typeof text === 'string') {
    return (
      <p
        className="font-medium text-xl"
        style={{ color }}
        dangerouslySetInnerHTML={{ __html: text }}
      />
    );
  }

  return (
    <div className="font-medium text-xl" style={{ color }}>
      {text}
    </div>
  );
};

export const ReportingPreview = ({ brand, questions = [], custom_report = false }) => {
  const candidate = DEFAULT_SAMPLE_CANDIDATE;
  const referee = DEFAULT_SAMPLE_REFEREE;

  const aiSummary = {
    overall_summary:
      'The candidate is a strong communicator and team player. They are proactive and collaborative, and always maintain a positive attitude when working under pressure.',
    key_strengths: [
      {
        title: 'Communication',
        summary:
          'The candidate is a strong communicator and team player. They are proactive and collaborative, and always maintain a positive attitude when working under pressure.',
        evidence:
          'One referee mentioned that the candidate is a strong communicator and team player. They are proactive and collaborative, and always maintain a positive attitude when working under pressure.',
      },
      {
        title: 'Teamwork',
        summary:
          'The candidate is a strong team player. They are proactive and collaborative, and always maintain a positive attitude when working under pressure.',
        evidence:
          'One referee mentioned that the candidate is a strong team player. They are proactive and collaborative, and always maintain a positive attitude when working under pressure.',
      },
    ],
    areas_of_concern: [
      {
        title: 'Communication',
        summary:
          'The candidate is a strong communicator and team player. They are proactive and collaborative, and always maintain a positive attitude when working under pressure.',
        evidence:
          'One referee mentioned that the candidate is a strong communicator and team player. They are proactive and collaborative, and always maintain a positive attitude when working under pressure.',
      },
    ],
  };

  const displayQuestions = questions?.length > 0 ? questions : DEFAULT_SAMPLE_QUESTIONS;
  const check = DEFAULT_SAMPLE_CHECK;
  const metadataBorderColor = `${brand?.report_primary_text_color}60`;

  const headerContainerStyle =
    !brand?.report_banner || !brand?.report_header
      ? {
          backgroundImage: `radial-gradient(125% 140% at 50% 100%, #fff ${brand?.report_header ? '50%' : '60%'}, ${brand?.report_bg_color} 100%)`,
        }
      : undefined;

  const reportHeaderStyle = brand?.report_banner
    ? {
        backgroundImage: `linear-gradient(to bottom, rgba(255,255,255,0) 35%, rgba(255,255,255,0.25) 60%, rgba(255,255,255,0.75) 82%, #ffffff 100%), url('${brand?.report_banner}')`,
      }
    : undefined;

  const headerBarStyle = {
    backgroundColor: brand?.report_header_bgcolor,
    width: '100%',
    height: 100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: '0 32px',
  };

  return (
    <div className="min-h-screen">
      <div className="w-full flex flex-col gap-4" style={headerContainerStyle}>
        {brand?.report_header && (
          <div
            className="relative flex w-full h-[240px] overflow-hidden bg-cover bg-center"
            style={reportHeaderStyle}>
            <div className="flex flex-col justify-center items-center w-full">
              <div style={headerBarStyle}>
                <div style={{ textAlign: 'right' }}>
                  <p
                    style={{
                      color: brand?.report_header_color,
                      fontSize: 11,
                      fontWeight: 400,
                      letterSpacing: '0.05em',
                      margin: '0 0 4px 0',
                    }}>
                    Professional Candidate Evaluation
                  </p>
                  <span
                    style={{
                      color: brand?.report_header_color,
                      fontSize: 24,
                      fontWeight: 700,
                      margin: 0,
                      letterSpacing: '-0.01em',
                    }}>
                    Reference Check Report
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
        {brand?.brand_logo && (
          <div
            className={`flex justify-center items-center w-full ${!brand?.report_header ? 'mt-32' : ''}`}>
            <img
              src={brand?.brand_logo}
              alt={brand?.name}
              style={{
                maxHeight: 100,
                minHeight: 100,
                maxWidth: 260,
                objectFit: 'contain',
              }}
            />
          </div>
        )}
        <div
          className="rounded-3xl flex flex-col items-center justify-center p-8 gap-8 mx-8"
          style={{ backgroundColor: 'rgb(245, 248, 253)' }}>
          <div className="text-center flex flex-col items-center justify-center gap-2">
            <span className="text-4xl font-bold">{candidate.name}</span>
            <span className="text-medium text-md">
              {candidate.job_role} <span className="text-xl">•</span> {candidate.department}
            </span>
            <span className="text-medium text-md">
              {candidate.email} <span className="text-xl">•</span> {candidate.contact_number}
            </span>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center gap-2">
          <span className="text-4xl font-bold" style={{ color: brand?.report_primary_text_color }}>
            {displayQuestions.length}
          </span>
          <span className="font-medium text-xl" style={{ color: 'rgb(98, 116, 142)' }}>
            Total Questions
          </span>
        </div>

        <div className="flex flex-row flex-wrap items-center justify-center gap-8 mx-8 py-8">
          <ReportMetadataField
            icon={Calendar01Icon}
            label="Created Date"
            borderColor={metadataBorderColor}>
            <div className="text-lg font-normal" style={{ color: '#1d293d' }}>
              {check.created_at}
            </div>
          </ReportMetadataField>

          {check.completed_at && (
            <ReportMetadataField
              icon={Calendar01Icon}
              label="Completed Date"
              borderColor={metadataBorderColor}>
              <div className="text-lg font-normal" style={{ color: '#1d293d' }}>
                {check.completed_at}
              </div>
            </ReportMetadataField>
          )}

          {check.completed_in && (
            <ReportMetadataField
              icon={Clock01Icon}
              label="Time to Complete"
              borderColor={metadataBorderColor}>
              <div className="text-lg font-normal" style={{ color: '#1d293d' }}>
                {check.completed_in}
              </div>
            </ReportMetadataField>
          )}

          <ReportMetadataField
            icon={Loading03Icon}
            label="Status"
            borderColor={metadataBorderColor}>
            <span
              className="text-lg font-normal inline-flex items-center gap-2"
              style={{ color: '#17B26A' }}>
              <span className="inline-block w-2 h-2 rounded-full shrink-0 bg-[#17B26A]" />
              Complete
            </span>
          </ReportMetadataField>
        </div>
      </div>

      <div className="flex flex-col gap-8 p-8" style={{ backgroundColor: 'rgb(245, 248, 253)' }}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-3">
            <p className="text-lg font-semibold m-0">Referee Details</p>
            <div className="w-full">
              <div className="border border-[#bdbdbd] rounded-2xl p-4 bg-white">
                <div className="flex justify-between items-center">
                  <p className="text-lg font-semibold m-0">
                    {check.anonymize_referee ? 'Referee (Anonymized)' : referee.name}
                  </p>
                  <div className="px-2 py-0.5 text-lg rounded-xl border border-neutral-4 bg-neutral-1 text-neutral-7">
                    Referee
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 mt-3">
                  {!check.anonymize_referee && (
                    <>
                      <RefereeDetailRow icon={Mail01Icon}>
                        <p className="text-lg font-normal m-0 underline text-neutral-7">
                          {referee.email}
                        </p>
                        {referee.has_company_email ? (
                          <VerifiedBadge />
                        ) : (
                          <RefereeWarning text="Personal email used" />
                        )}
                      </RefereeDetailRow>

                      <RefereeDetailRow icon={SmartPhone01Icon}>
                        <p className="text-lg font-normal m-0 text-neutral-7">
                          {referee.contact_number}
                        </p>
                      </RefereeDetailRow>
                    </>
                  )}

                  {referee.linkedin_profile ? (
                    <RefereeDetailRow icon={Linkedin01Icon}>
                      <p className="text-lg font-normal m-0 text-neutral-7">
                        {referee.linkedin_profile}
                      </p>
                      <VerifiedBadge />
                    </RefereeDetailRow>
                  ) : (
                    <RefereeDetailRow icon={Linkedin01Icon}>
                      <RefereeWarning text="No LinkedIn profile provided" />
                    </RefereeDetailRow>
                  )}

                  {!check.anonymize_referee && (
                    <RefereeDetailRow icon={ComputerIcon}>
                      <p className="text-lg font-normal m-0 text-neutral-7">IP: {referee.ip}</p>
                      {referee.ip === candidate.ip ? (
                        <RefereeWarning text="Same IP as candidate" />
                      ) : (
                        <VerifiedBadge />
                      )}
                    </RefereeDetailRow>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {brand?.ff_ai_reference_summary && !custom_report && (
          <div className="bg-neutral-1 rounded-3xl p-8 border border-neutral-4 shadow-sm">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <HugeiconsIcon
                  icon={ArtificialIntelligence08Icon}
                  size={20}
                  className="text-primary-4"
                  strokeWidth={2}
                />
                <span className="text-2xl font-medium text-neutral-7">AI Reference Summary</span>
              </div>
              <span className="text-xl text-neutral-6 font-regular">
                {aiSummary?.overall_summary}
              </span>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                {aiSummary?.key_strengths.map((keyStrength, index) => (
                  <div key={index} className="bg-neutral-2 rounded-2xl p-4 border border-neutral-4">
                    <div className="flex items-center gap-2">
                      <HugeiconsIcon
                        icon={ThumbsUpIcon}
                        size={24}
                        className="text-green-2 bg-green-1 rounded-xl p-2"
                      />
                      <span className="text-sm font-medium text-neutral-6 uppercase">Strength</span>
                    </div>
                    <div className="flex flex-col gap-2">
                      <span className="text-xl font-medium text-neutral-7">
                        {keyStrength.summary}.&nbsp;
                        <span className="text-xl font-light text-neutral-6">
                          {keyStrength.evidence}
                        </span>
                      </span>
                    </div>
                  </div>
                ))}
                {aiSummary?.areas_of_concern.map((concern, index) => (
                  <div key={index} className="bg-neutral-2 rounded-2xl p-4 border border-neutral-4">
                    <div className="flex items-center gap-2">
                      <HugeiconsIcon
                        icon={Alert01Icon}
                        size={24}
                        className="text-red-2 bg-red-1 rounded-xl p-2"
                      />
                      <span className="text-sm font-medium text-neutral-6 uppercase">
                        Area of Concern
                      </span>
                    </div>
                    <div className="flex flex-col gap-2">
                      <span className="text-xl font-medium text-neutral-7">
                        {concern.summary}.&nbsp;
                        <span className="text-xl font-light text-neutral-6">
                          {concern.evidence}
                        </span>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-4 pt-4 border-t border-neutral-4 border-dashed">
                <div className="flex items-center gap-2">
                  <HugeiconsIcon
                    icon={InformationCircleIcon}
                    size={16}
                    className="text-text-tertiary"
                  />
                  <span className="text-lg font-regular text-text-tertiary">
                    Generated based on the referee's responses using AI
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-regular text-text-tertiary border-r border-neutral-4 pr-2">
                    Summary can be inaccurate
                  </span>
                  <a
                    href="mailto:support@checkmate.tech"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lg font-medium text-primary-4">
                    Send feedback
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="flex flex-col gap-8">
          {displayQuestions.map((question, index) => {
            const questionReferee = question.referee ?? DEFAULT_SAMPLE_REFEREE;
            const refereeInitials =
              questionReferee.initials || getRefereeInitials(questionReferee.name);
            const questionAnswer = custom_report ? '' : DEFAULT_SAMPLE_ANSWER;

            return (
              <div
                key={question.order ?? index}
                className="rounded-t-3xl overflow-hidden"
                style={{ borderLeft: `5px solid ${brand?.report_bg_color}` }}>
                <div
                  className="px-8 py-4 flex flex-col gap-2"
                  style={{ backgroundColor: brand?.report_bg_color }}>
                  <div className="flex flex-row justify-between items-center gap-4">
                    <div className="flex flex-row items-center gap-4">
                      <div className="text-lg font-semibold w-8 h-8 flex items-center justify-center rounded-full bg-neutral-1 shrink-0">
                        <span style={{ color: brand?.report_bg_color }}>
                          Q{question.order ?? index + 1}
                        </span>
                      </div>
                      {renderQuestionText(question.text, brand?.report_secondary_color)}
                    </div>
                    <div className="px-4 py-2 bg-neutral-1 rounded-xl border border-neutral-4 text-neutral-6 text-lg shrink-0">
                      {getQuestionTypeLabel(question)}
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-4 p-8 bg-white">
                  <div className="flex flex-row gap-2 justify-start items-center">
                    <div
                      className="w-12 h-12 bg-neutral-2 rounded-full flex items-center justify-center border border-neutral-4 font-semibold"
                      style={{ color: '#717680' }}>
                      {refereeInitials}
                    </div>
                    <span className="font-semibold text-xl">{questionReferee.name}</span>
                  </div>

                  <div className="answer-row flex gap-4">
                    <p className="text-lg text-regular flex-1">{questionAnswer}</p>
                    <div className="flex-shrink-0" />
                  </div>

                  <p className="text-lg font-light text-text-tertiary italic">
                    This is a sample referee response shown for preview purposes only. Actual
                    referee feedback will appear here once references have been completed.
                  </p>
                </div>
              </div>
            );
          })}

          <div className="flex flex-col gap-4 p-8 border-t border-neutral-4 mt-4 items-center justify-center">
            <div className="flex flex-col justify-center items-center gap-8 mt-4">
              <p
                className="text-md text-regular text-center"
                style={{ color: 'rgb(98, 116, 142)' }}>
                Powered by
              </p>
              <img src="/assets/images/checkmate/icon-label.svg" style={{ width: '160px' }} />
            </div>

            <div className="bg-white p-8 rounded-3xl flex gap-4">
              <p className="text-xl font-semibold">Disclaimer</p>
              <p className="text-lg font-regular" style={{ color: 'rgb(98, 116, 142)' }}>
                Checkmate is committed to providing accurate and up-to-date information to the best
                of its abilities. However, please note that the information presented by Checkmate
                may not always be entirely accurate, complete, or current. Due to the dynamic nature
                of information and the vast amount of data available, it is possible that some
                information may be inaccurate, outdated, or subject to change. Checkmate does not
                assume any responsibility or liability for any errors, inaccuracies, omissions, or
                inconsistencies in the information provided. Users rely on the information provided
                by Checkmate at their own risk.
              </p>
            </div>

            <img
              src="https://checkmate-prod.s3.ap-southeast-2.amazonaws.com/assets/iso-logo.png"
              style={{ maxWidth: '50px' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
